import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { getBracketById, randomizeParticipantsAction } from '../redux/app';
import Wrapper from '../components/Wrapper';
import { StyleSheet, css } from 'aphrodite/no-important';
import BracketNode from '../components/BracketNode';
import {
  BASE_SPACING,
  FONTS,
  CENTER,
  TRANSPARENT,
  WHITE,
  DOVE_GRAY,
} from '../styles';
import { navigateTo } from '../utils';
import Icon from '../components/Icon';
import Text from '../components/Text';
import Button, { TYPES } from '../components/Button';

const filterNodes = (nodes = [], level) => {
  const initial = nodes.filter(({ initial }) => initial);

  if (!level) {
    return initial;
  }

  const recursiveFindNodes = (_nodes, index) => {
    const nexts = _nodes.map(({ next }) => next);
    const currentNodes = nodes.filter(({ id }) => nexts.includes(id));

    if (index - 1 > 0) {
      return recursiveFindNodes(currentNodes, index - 1);
    }

    return currentNodes;
  };

  return recursiveFindNodes(initial, level);
};

const s = StyleSheet.create({
  root: {
    width: '100%',
    display: 'grid',
    padding: BASE_SPACING,
    gridTemplateColumns: 'auto auto',
    columnGap: BASE_SPACING,
    rowGap: BASE_SPACING,
    gridAutoFlow: 'column',
  },
  navigation: {
    height: 60,
    display: 'flex',
    alignItems: CENTER,
    justifyContent: 'space-between',
    width: '100%',
    boxShadow: '9px 6px 9px 0px rgba(0,0,0,0.1)',
  },
  randomizeContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: BASE_SPACING,
    paddingBottom: 0,
  },
  title: {
    fontSize: 18,
    ...FONTS.BOLD,
    textAlign: CENTER,
  },
  button: {
    borderWidth: 0,
    appearance: 'none',
    backgroundColor: TRANSPARENT,
    outline: 0,
    padding: BASE_SPACING,
  },
  icon: {
    width: 20,
    height: 20,
  },
  inProgress: {
    color: WHITE,
    backgroundColor: DOVE_GRAY,
  },
});

const TREE_LENGTH = {
  8: 3,
  16: 4,
  32: 5,
  64: 6,
  128: 7,
};

const BracketConfigurator = ({ id }) => {
  const [index, setIndex] = useState(0);
  const [isInProgress, setInProgress] = useState(0);
  const { nodes, participantCount } = useSelector((state) =>
    getBracketById(state, id)
  );
  const nodesToRender = filterNodes(nodes, index);
  const dispatch = useDispatch();
  const hasWinner = nodesToRender.find(({ winner }) => winner);
  return (
    <>
      <nav className={css(s.navigation)}>
        <button
          className={css(s.button)}
          onClick={() => setIndex(Math.max(index - 1, 0))}
        >
          <Icon name="arrow-left" __className={s.icon} />
        </button>
        {!!nodesToRender.length && (
          <Text
            messageId={`app.tree.node.navigator.${nodesToRender.length}`}
            className={css(s.title)}
          />
        )}
        <button
          className={css(s.button)}
          onClick={() =>
            setIndex(Math.min(TREE_LENGTH[participantCount] - 1, index + 1))
          }
        >
          <Icon name="arrow-right" __className={s.icon} />
        </button>
      </nav>
      {index === 0 && !hasWinner && (
        <div className={css(s.randomizeContainer)}>
          <Button
            messageId="app.randomize"
            __type={TYPES.SECONDARY}
            __className={isInProgress && s.inProgress}
            onClick={() => {
              setInProgress(true);
              dispatch(randomizeParticipantsAction(id)).finally(() => {
                setInProgress(false);
              });
            }}
          />
        </div>
      )}
      <Wrapper
        __className={s.root}
        style={{
          gridTemplateRows: `repeat(${nodesToRender.length / 2}, auto)`,
          gridTemplateColumns:
            nodesToRender.length === 1
              ? '100%'
              : `calc(50% - ${BASE_SPACING / 2}px) calc(50% - ${
                  BASE_SPACING / 2
                }px)`,
        }}
      >
        {nodesToRender.map((node, i) => (
          <BracketNode
            {...node}
            key={node.id}
            index={i * 2}
            extraGap={(i + 1) % 2 === 0 && nodesToRender.length !== 2}
            onClick={() =>
              navigateTo(
                `/tree/edit/${id}/node/${node.id}${window.location.hash}`
              )
            }
          />
        ))}
      </Wrapper>
    </>
  );
};

BracketConfigurator.propTypes = {
  id: PropTypes.string,
};

export default BracketConfigurator;
